import { Alert, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import {
  CustomSignupLinkForm,
  CustomSignupLinkFormValues,
} from 'src/components/CustomSignupLinkModal/custom-signup-link-form';
import { CustomSignupLinkResult } from 'src/components/CustomSignupLinkModal/custom-signup-link-result';
import { Address, useGenerateCustomSignupLink, useOfferedProducts } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useCopyToClipboard, useNotifications } from 'src/utils/hooks';

const DEFAULT_ERROR_MESSAGE =
  'Unable to generate a custom signup link right now. Please try again.';
const CUSTOM_SIGNUP_LINK_DEFAULT_VALUES: CustomSignupLinkFormValues = {
  partnerProductId: '',
  amountDollars: 0,
  intervalUnit: 'month',
};

export const CustomSignupLinkModal = () => {
  const { closeModal } = useModalMethods(Modals.CustomSignupLink);
  const { isOpened, modalProps } = useModalState(Modals.CustomSignupLink);
  const { showErrorAlert } = useNotifications();
  const { copyToClipboard } = useCopyToClipboard({
    successMessage: 'Checkout URL copied.',
  });
  const { generateCustomSignupLink, loading } = useGenerateCustomSignupLink();

  const [generatedUrl, setGeneratedUrl] = useState('');

  const formMethods = useForm<CustomSignupLinkFormValues>({
    defaultValues: CUSTOM_SIGNUP_LINK_DEFAULT_VALUES,
  });

  const address = modalProps?.address as Address | null | undefined;
  const { offeredProducts, loading: isLoadingProducts } = useOfferedProducts({
    partnerId: address?.partnerId,
    brandId: address?.brandId,
  });

  const hasExistingSubscription = Boolean(modalProps?.addressSubscriptionId);
  const hasEligibleProducts = offeredProducts.length > 0;

  const handleClose = useCallback(() => {
    formMethods.reset();
    setGeneratedUrl('');
    closeModal();
  }, [closeModal, formMethods]);

  const handleSubmit = useCallback(
    async (values: CustomSignupLinkFormValues) => {
      if (!address?.addressId || isNullish(address.partnerId)) {
        showErrorAlert('Address context is missing. Reopen the dialog and try again.');
        return;
      }

      try {
        setGeneratedUrl('');

        const result = await generateCustomSignupLink({
          addressId: address.addressId,
          partnerId: address.partnerId,
          partnerProductId: values.partnerProductId,
          amountCents: Math.round(values.amountDollars * 100),
          intervalUnit: values.intervalUnit,
          intervalCount: 1,
          cancelUrl: window.location.href,
          successUrl: window.location.href,
        });

        setGeneratedUrl(result.url);
      } catch (error) {
        showErrorAlert(getErrorMessage(error));
      }
    },
    [address, generateCustomSignupLink, showErrorAlert],
  );

  const handleCopy = useCallback(async () => {
    try {
      await copyToClipboard(generatedUrl);
    } catch {
      showErrorAlert('Copy failed. Please try again.');
    }
  }, [copyToClipboard, generatedUrl, showErrorAlert]);

  const handleOpen = useCallback(() => {
    try {
      const openedWindow = window.open(generatedUrl, '_blank', 'noopener,noreferrer');

      if (!openedWindow) {
        throw new Error('Window was blocked');
      }
    } catch {
      showErrorAlert('Unable to open the checkout URL in a new tab.');
    }
  }, [generatedUrl, showErrorAlert]);

  const renderBody = () => {
    if (!address?.addressId) {
      return (
        <Alert severity="error">Address context is missing. Close the dialog and try again.</Alert>
      );
    }

    if (hasExistingSubscription) {
      return (
        <Alert severity="warning">
          This address already has an active subscription, so a new checkout link cannot be
          generated.
        </Alert>
      );
    }

    if (!isLoadingProducts && !hasEligibleProducts) {
      return (
        <Alert severity="info">
          No eligible membership products are configured for this address brand.
        </Alert>
      );
    }

    if (generatedUrl) {
      return (
        <CustomSignupLinkResult
          url={generatedUrl}
          onCopy={handleCopy}
          onClose={handleClose}
          onOpen={handleOpen}
        />
      );
    }

    return (
      <CustomSignupLinkForm
        formMethods={formMethods}
        offeredProducts={offeredProducts}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        isSubmitting={loading}
      />
    );
  };

  return (
    <Dialog
      open={isOpened}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ 'data-testid': 'custom-signup-link-dialog' }}
    >
      <DialogTitle>Custom signup link</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            Choose an offered membership, enter a custom amount, and generate a Stripe checkout
            link that returns to this address after success or cancel.
          </Typography>
          {renderBody()}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return DEFAULT_ERROR_MESSAGE;
}
